/**
 * PasoPanelFotografico — Paso 3 del IE. Tarjetas de foto con subida a R2 presign+PUT.
 * Cada tarjeta: imagen (uploadImagen → url) + título + descripción + quitar.
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, Upload, Camera, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button, buttonVariants } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { isPortrait } from "@/shared/lib/image-frame.js";
import { getBlobsForReport } from "@/features/offline-sync/services/blob-queue.js";
import {
  preparePhotoFile,
  uploadOrEnqueue,
  retryQueuedPhotos,
  blobStateForPlaceholder,
} from "./upload-or-enqueue.js";
import type { IeFoto } from "../../types.js";
import { LabeledField, LabeledTextarea, labelClass } from "./fields.js";
import { restaurarPredeterminados } from "./ie-payload.js";

export function PasoPanelFotografico(props: {
  value: IeFoto[];
  onChange: (next: IeFoto[]) => void;
  onError: (msg: string | null) => void;
  /** Report localId — needed to queue photos as IDB blobs when offline. */
  localId: string;
  disabled?: boolean;
}) {
  const { value, onChange, onError, localId, disabled } = props;

  function agregar() {
    onChange([...value, { url: "", titulo: "", descripcion: "" }]);
  }

  function quitar(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function set(index: number, key: keyof IeFoto, v: string) {
    onChange(value.map((f, i) => (i === index ? { ...f, [key]: v } : f)));
  }

  function setVertical(index: number, v: boolean) {
    onChange(value.map((f, i) => (i === index ? { ...f, vertical: v } : f)));
  }

  return (
    <div className="space-y-4">
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Sin fotos todavía. Agrega la primera con el botón de abajo.
        </p>
      ) : (
        <ul className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {value.map((foto, i) => (
            <li key={i} className="space-y-3 rounded-lg border border-border bg-background/60 p-4">
              <div className="flex items-center justify-between">
                <span className={labelClass}>Foto {i + 1}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => quitar(i)}
                  className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em] text-destructive"
                >
                  <Trash2 className="size-3.5" aria-hidden />
                  Quitar
                </Button>
              </div>
              <FotoUpload
                index={i}
                url={foto.url}
                localId={localId}
                disabled={disabled}
                onUrl={(url) => set(i, "url", url)}
                onVertical={(v) => setVertical(i, v)}
                onError={onError}
              />
              <LabeledField
                id={`foto-${i}-titulo`}
                label="Título"
                value={foto.titulo}
                onChange={(v) => set(i, "titulo", v)}
                disabled={disabled}
                placeholder="Título del slot"
              />
              <LabeledTextarea
                id={`foto-${i}-descripcion`}
                label="Descripción"
                value={foto.descripcion}
                onChange={(v) => set(i, "descripcion", v)}
                disabled={disabled}
                rows={2}
                placeholder="Descripción de la foto"
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={agregar}
          className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
        >
          <Plus className="size-3.5" aria-hidden />
          Agregar foto
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onChange(restaurarPredeterminados())}
          className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.12em]"
        >
          Restaurar 7 sugeridos
        </Button>
      </div>
    </div>
  );
}

/** Subida de una foto: thumbnail + estado subiendo + guarda de 5 MB. */
function FotoUpload(props: {
  index: number;
  url: string;
  localId: string;
  disabled?: boolean;
  onUrl: (url: string) => void;
  onVertical: (v: boolean) => void;
  onError: (msg: string | null) => void;
}) {
  const { index, url, localId, disabled, onUrl, onVertical, onError } = props;
  const [subiendo, setSubiendo] = useState(false);
  const [reintentando, setReintentando] = useState(false);
  // Local object URL for an offline-captured photo, so the thumbnail shows
  // immediately while the real value is an `idb-blob:` placeholder.
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    e.target.value = ""; // permite re-seleccionar el mismo archivo
    if (!picked) return;
    // Compression runs BEFORE the 5MB gate (and before the orientation gate):
    // large phone photos are re-encoded to JPEG ≤1600px first, so the gates
    // operate on the final bytes that will be uploaded/enqueued. Fail-open:
    // an undecodable photo keeps its original bytes and the gates still apply.
    const prepared = await preparePhotoFile(picked);
    if (prepared.kind === "error") {
      onError(prepared.message);
      return;
    }
    const file = prepared.file;
    // Gate — reject vertical on pick/capture (gallery AND camera use this path):
    // panel cards must stay horizontal. Leave the slot untouched on reject.
    // Toast (auto-dismiss) so the message is visible even when scrolled down on
    // mobile. Fail-open only if the dimensions can't be read (the <img> onLoad
    // backstop still catches it once rendered).
    try {
      if (await isPortrait(file)) {
        toast.error("La foto es vertical y no está permitida. Usa una foto horizontal.");
        return;
      }
    } catch {
      // unreadable — allow through; backstop will flag if it renders vertical
    }
    onError(null);
    setSubiendo(true);
    await procesar(file);
  }

  async function procesar(file: File) {
    try {
      // Upload, falling back to the offline blob queue on network/5xx failure —
      // branching is on the actual error (isNetworkOrServerFailure), and the
      // blob-queue IS the retry mechanism (flush re-uploads on reconnect).
      const result = await uploadOrEnqueue(file, localId);
      if (result.kind === "uploaded") {
        if (localPreview) URL.revokeObjectURL(localPreview);
        setLocalPreview(null);
        onUrl(result.url);
      } else if (result.kind === "enqueued") {
        if (localPreview) URL.revokeObjectURL(localPreview);
        setLocalPreview(URL.createObjectURL(file));
        onUrl(result.url);
        toast.success("Foto guardada en este equipo, se subirá sola");
      } else {
        onError(result.message);
      }
    } catch (err) {
      onError(friendlyMessage(err));
    } finally {
      setSubiendo(false);
    }
  }

  /** Manual retry: runs a single-flight flush, then reports per-photo state. */
  async function reintentar() {
    setReintentando(true);
    try {
      await retryQueuedPhotos(localId);
      const state = await blobStateForPlaceholder(url);
      if (state === "uploaded") {
        // Resolve the placeholder to the real URL so the thumbnail renders now.
        const blob = (await getBlobsForReport(localId)).find(
          (b) => `idb-blob:${b.ownerLocalId}:${b.fieldPath}` === url,
        );
        if (blob?.remoteUrl) {
          if (localPreview) URL.revokeObjectURL(localPreview);
          setLocalPreview(null);
          onUrl(blob.remoteUrl);
        }
        toast.success("Foto subida correctamente.");
      } else if (state === "pending") {
        toast.info("Aún no se pudo subir. Se reintentará sola cuando vuelva la conexión.");
      } else {
        toast.error("No encontramos la foto guardada en este equipo. Vuelve a agregarla.");
      }
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setReintentando(false);
    }
  }

  // What to render in the thumbnail: the local capture, else a real http URL.
  // An `idb-blob:` placeholder with no local capture (e.g. draft reopened) is
  // shown as a "saved offline" state rather than a broken image.
  const esPlaceholder = url.startsWith("idb-blob:");
  const displaySrc = localPreview ?? (url && !esPlaceholder ? url : null);

  return (
    <div className="grid gap-1.5">
      <span className={labelClass}>Imagen</span>
      {/* Área de preview full-width */}
      <div className="relative w-full overflow-hidden rounded-md border border-border bg-secondary">
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={`Foto ${index + 1}`}
            className="aspect-[4/3] w-full object-contain"
            // Gate 2 (backstop) — the rendered image is the source of truth for
            // orientation. Flags legacy/draft verticals the upload gate never saw.
            onLoad={(e) => onVertical(e.currentTarget.naturalHeight > e.currentTarget.naturalWidth)}
          />
        ) : esPlaceholder ? (
          <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-1 text-muted-foreground">
            <ImageIcon className="size-10" aria-hidden />
            <span className="font-mono text-[0.625rem] uppercase tracking-[0.1em]">
              Guardada offline
            </span>
          </div>
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center text-muted-foreground">
            <ImageIcon className="size-10" aria-hidden />
          </div>
        )}
        {/* Botones flotantes: galería + cámara (o el spinner al subir) */}
        {subiendo ? (
          <span
            className={cn(
              "absolute bottom-2 right-2",
              buttonVariants({ variant: "outline", size: "sm" }),
              "gap-1.5 bg-card/90 font-mono text-[0.6875rem] uppercase tracking-[0.1em] backdrop-blur-sm",
            )}
          >
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Subiendo…
          </span>
        ) : (
          <div className="absolute bottom-2 right-2 flex gap-1.5">
            {/* Galería / archivos — sin `capture` para abrir el selector de fotos */}
            <label
              className={cn(
                "cursor-pointer",
                buttonVariants({ variant: "outline", size: "sm" }),
                "gap-1.5 bg-card/90 font-mono text-[0.6875rem] uppercase tracking-[0.1em] backdrop-blur-sm",
                disabled && "pointer-events-none opacity-60",
              )}
            >
              <Upload className="size-3.5" aria-hidden />
              {url ? "Cambiar" : "Galería"}
              <input
                type="file"
                accept="image/*"
                onChange={onFile}
                disabled={disabled}
                className="sr-only"
              />
            </label>
            {/* Cámara — `capture` abre la cámara directamente en móvil (solo móvil) */}
            <label
              className={cn(
                "cursor-pointer sm:hidden",
                buttonVariants({ variant: "outline", size: "sm" }),
                "gap-1.5 bg-card/90 font-mono text-[0.6875rem] uppercase tracking-[0.1em] backdrop-blur-sm",
                disabled && "pointer-events-none opacity-60",
              )}
              aria-label="Tomar foto con la cámara"
            >
              <Camera className="size-3.5" aria-hidden />
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onFile}
                disabled={disabled}
                className="sr-only"
              />
            </label>
          </div>
        )}
      </div>
      {/* Reintento manual para fotos encoladas (placeholder idb-blob:) */}
      {esPlaceholder && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled || reintentando || subiendo}
          onClick={reintentar}
          className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
        >
          {reintentando ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <RefreshCw className="size-3.5" aria-hidden />
          )}
          {reintentando ? "Subiendo…" : "Reintentar subida"}
        </Button>
      )}
      <p className="font-mono text-[0.625rem] uppercase tracking-[0.1em] text-muted-foreground/60">
        PNG o JPG · máx. 5 MB · solo horizontales
      </p>
    </div>
  );
}
